import { useState } from "react";
import { Globe, Plus, Trash2, UtensilsCrossed, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Mini-site editor for claimed businesses (Marketplace Phase 3):
   tagline, about, booking/order links, menu builder and custom links. */

const empty = { tagline: "", about: "", booking_url: "", order_url: "", menu_sections: [], links: [] };

export const MinisiteEditor = ({ biz, onSaved }) => {
  const [ms, setMs] = useState({ ...empty, ...(biz.minisite || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setMs((m) => ({ ...m, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/marketplace/businesses/${biz.id}/minisite`, ms);
      toast.success("Mini-site published");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save mini-site"); }
    finally { setBusy(false); }
  };

  const addSection = () => set("menu_sections", [...ms.menu_sections, { title: "", items: [] }]);
  const setSection = (i, patch) => set("menu_sections", ms.menu_sections.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const delSection = (i) => set("menu_sections", ms.menu_sections.filter((_, idx) => idx !== i));
  const addItem = (i) => setSection(i, { items: [...ms.menu_sections[i].items, { name: "", price: "", description: "" }] });
  const setItem = (i, j, patch) => setSection(i, { items: ms.menu_sections[i].items.map((it, idx) => idx === j ? { ...it, ...patch } : it) });
  const delItem = (i, j) => setSection(i, { items: ms.menu_sections[i].items.filter((_, idx) => idx !== j) });

  const inputCls = "border border-zinc-300 px-3 py-2 text-sm w-full";

  return (
    <div className="mt-6 border-t border-zinc-200 pt-4" data-testid={`minisite-editor-${biz.id}`}>
      <div className="flex items-center gap-2 font-heading font-900 mb-1"><Globe className="w-4 h-4 text-violet-600" /> Your Mini-Site</div>
      <p className="text-xs text-muted-foreground mb-3">Build your page on TravelerPulse — menu, booking and ordering links shown to every traveler visiting your profile.</p>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="sm:col-span-2"><span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Tagline</span>
          <input value={ms.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Best espresso before your gate" data-testid="minisite-tagline" className={inputCls + " mt-1"} /></label>
        <label className="sm:col-span-2"><span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">About your business</span>
          <textarea rows={3} value={ms.about} onChange={(e) => set("about", e.target.value)} data-testid="minisite-about" className={inputCls + " mt-1"} /></label>
        <label><span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Booking / reservation URL</span>
          <input value={ms.booking_url} onChange={(e) => set("booking_url", e.target.value)} placeholder="https://…" data-testid="minisite-booking" className={inputCls + " mt-1"} /></label>
        <label><span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Online ordering URL</span>
          <input value={ms.order_url} onChange={(e) => set("order_url", e.target.value)} placeholder="https://…" data-testid="minisite-order" className={inputCls + " mt-1"} /></label>
      </div>

      {/* Menu builder */}
      <div className="mt-4">
        <div className="flex items-center gap-2 text-sm font-700"><UtensilsCrossed className="w-4 h-4 text-violet-600" /> Menu
          <button onClick={addSection} data-testid="minisite-add-section" className="ml-2 inline-flex items-center gap-1 text-xs text-violet-700 hover:underline"><Plus className="w-3.5 h-3.5" /> Add section</button>
        </div>
        {ms.menu_sections.map((s, i) => (
          <div key={i} className="border border-violet-200 bg-violet-50/40 p-3 mt-2" data-testid={`menu-section-${i}`}>
            <div className="flex gap-2 items-center">
              <input value={s.title} onChange={(e) => setSection(i, { title: e.target.value })} placeholder="Section (e.g. Breakfast, Drinks)" data-testid={`menu-section-title-${i}`} className={inputCls} />
              <button onClick={() => delSection(i)} className="text-rose-600 p-1" data-testid={`menu-section-del-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            {s.items.map((it, j) => (
              <div key={j} className="flex flex-wrap gap-2 mt-2 items-center">
                <input value={it.name} onChange={(e) => setItem(i, j, { name: e.target.value })} placeholder="Item name" data-testid={`menu-item-name-${i}-${j}`} className="border border-zinc-300 px-2 py-1.5 text-sm flex-1 min-w-[140px]" />
                <input value={it.price} onChange={(e) => setItem(i, j, { price: e.target.value })} placeholder="$0.00" className="border border-zinc-300 px-2 py-1.5 text-sm w-20" />
                <input value={it.description} onChange={(e) => setItem(i, j, { description: e.target.value })} placeholder="Description (optional)" className="border border-zinc-300 px-2 py-1.5 text-sm flex-1 min-w-[160px]" />
                <button onClick={() => delItem(i, j)} className="text-muted-foreground hover:text-rose-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button onClick={() => addItem(i)} data-testid={`menu-add-item-${i}`} className="mt-2 inline-flex items-center gap-1 text-xs text-violet-700 hover:underline"><Plus className="w-3.5 h-3.5" /> Add item</button>
          </div>
        ))}
      </div>

      {/* Custom links */}
      <div className="mt-4">
        <div className="flex items-center gap-2 text-sm font-700"><CalendarCheck className="w-4 h-4 text-violet-600" /> Extra links
          <button onClick={() => set("links", [...ms.links, { label: "", url: "" }])} data-testid="minisite-add-link" className="ml-2 inline-flex items-center gap-1 text-xs text-violet-700 hover:underline"><Plus className="w-3.5 h-3.5" /> Add link</button>
        </div>
        {ms.links.map((l, i) => (
          <div key={i} className="flex gap-2 mt-2 items-center">
            <input value={l.label} onChange={(e) => set("links", ms.links.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} placeholder="Label (e.g. Loyalty program)" className="border border-zinc-300 px-2 py-1.5 text-sm w-48" />
            <input value={l.url} onChange={(e) => set("links", ms.links.map((x, idx) => idx === i ? { ...x, url: e.target.value } : x))} placeholder="https://…" className="border border-zinc-300 px-2 py-1.5 text-sm flex-1" />
            <button onClick={() => set("links", ms.links.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-rose-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={busy} data-testid="minisite-save" className="mt-4 bg-violet-700 text-white px-5 py-2 text-sm font-700 hover:bg-violet-600 disabled:opacity-50 transition-colors">
        {busy ? "Publishing…" : "Publish mini-site"}
      </button>
    </div>
  );
};

/* Public mini-site display on the business page. */
export const MinisiteView = ({ minisite }) => {
  if (!minisite) return null;
  const { tagline, about, booking_url, order_url, menu_sections = [], links = [] } = minisite;
  const hasMenu = menu_sections.some((s) => s.title && s.items.length);
  if (!tagline && !about && !booking_url && !order_url && !hasMenu && !links.length) return null;
  return (
    <div data-testid="minisite-view">
      {tagline && <p className="text-lg text-violet-800 font-600 italic mt-1" data-testid="minisite-view-tagline">"{tagline}"</p>}
      {(booking_url || order_url) && (
        <div className="flex flex-wrap gap-2 mt-3">
          {booking_url && <a href={booking_url} target="_blank" rel="noreferrer" data-testid="minisite-book-btn"
            className="inline-flex items-center gap-1.5 bg-violet-700 text-white px-4 py-2 text-sm font-700 hover:bg-violet-600"><CalendarCheck className="w-4 h-4" /> Book a table</a>}
          {order_url && <a href={order_url} target="_blank" rel="noreferrer" data-testid="minisite-order-btn"
            className="inline-flex items-center gap-1.5 bg-emerald-700 text-white px-4 py-2 text-sm font-700 hover:bg-emerald-600"><UtensilsCrossed className="w-4 h-4" /> Order online</a>}
          {links.filter((l) => l.label && l.url).map((l) => (
            <a key={l.url} href={l.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 border border-violet-300 text-violet-700 px-4 py-2 text-sm font-600 hover:bg-violet-50">{l.label}</a>
          ))}
        </div>
      )}
      {about && <p className="text-sm text-zinc-700 mt-4 whitespace-pre-line" data-testid="minisite-view-about">{about}</p>}
      {hasMenu && (
        <div className="mt-6" data-testid="minisite-view-menu">
          <h3 className="font-heading font-900 text-xl flex items-center gap-2 mb-3"><UtensilsCrossed className="w-5 h-5 text-violet-600" /> Menu</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {menu_sections.filter((s) => s.title && s.items.length).map((s, i) => (
              <div key={i} className="border border-zinc-300 bg-white p-4">
                <div className="font-700 text-violet-800 uppercase text-xs tracking-widest mb-2">{s.title}</div>
                {s.items.filter((it) => it.name).map((it, j) => (
                  <div key={j} className="py-1.5 border-b border-zinc-100 last:border-0">
                    <div className="flex items-baseline gap-2 text-sm">
                      <span className="font-600">{it.name}</span>
                      <span className="flex-1 border-b border-dotted border-zinc-300" />
                      {it.price && <span className="font-mono font-700">{it.price}</span>}
                    </div>
                    {it.description && <div className="text-xs text-muted-foreground">{it.description}</div>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
